import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  InitialStateMinimumRequirements_GraphQl,
  LegacyMagento_ShippingInformation_CartAddressInput,
} from "../../../Common";

import { DataMutation_ShippingMemberSetAuthorityToLeave_QueryKey } from "./data-mutation--shipping-member-set-authority-to-leave--query-key-factory";

export interface DataMutationShippingMemberSetAuthorityToLeaveConsumerInterface {
  data: DataMutationShippingMemberSetAuthorityToLeave_Result["data"];
  status: DataMutationShippingMemberSetAuthorityToLeave_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingMemberSetAuthorityToLeaveConsumerInterface;
const DataMutationShippingMemberSetAuthorityToLeaveContext =
  React.createContext(defaultValue);

export interface DataMutationShippingMemberSetAuthorityToLeave_ControllerInterface {
  mutate: DataMutationShippingMemberSetAuthorityToLeave_Result["mutate"];
  mutateAsync: DataMutationShippingMemberSetAuthorityToLeave_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingMemberSetAuthorityToLeave_ControllerInterface;
const DataMutationShippingMemberSetAuthorityToLeave_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationShippingMemberSetAuthorityToLeave_Request {
  address: LegacyMagento_ShippingInformation_CartAddressInput;
}
export interface DataMutationShippingMemberSetAuthorityToLeave_Response {
  done: boolean;
}
export type DataMutationShippingMemberSetAuthorityToLeave_Result =
  ReactQuery.UseMutationResult<
    DataMutationShippingMemberSetAuthorityToLeave_Response,
    unknown,
    DataMutationShippingMemberSetAuthorityToLeave_Request,
    unknown
  >;

export interface DataMutationShippingMemberSetAuthorityToLeaveProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingMemberSetAuthorityToLeaveProvider: React.FunctionComponent<
  DataMutationShippingMemberSetAuthorityToLeaveProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_ShippingMemberSetAuthorityToLeave_QueryKey.shippingMemberSetAuthorityToLeave();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    DataMutationShippingMemberSetAuthorityToLeave_Response,
    DataMutationShippingMemberSetAuthorityToLeave_Request
  > = React.useCallback(
    async (request) => {
      const headers = {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };

      const { data } = await axios.post(
        `/rest/${initialState.store_code}/V1/carts/mine/shipping-information`,
        {
          addressInformation: {
            shipping_address: request.address,
            extension_attributes: {
              authority_to_leave: true,
            },
          },
        },
        { headers }
      );

      if (data.error || data.errors) {
        const firstErrorMessage =
          (!!data.error ? data.error : "") ||
          (data.errors.length > 0
            ? !!data.errors[0].message
              ? data.errors[0].message
              : ""
            : "") ||
          "Unknown Error";

        throw new Error(firstErrorMessage);
      }

      return {
        done: true,
      };
    },
    [initialState]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn);

  const value: DataMutationShippingMemberSetAuthorityToLeaveConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
      }),
      [mutation.data, mutation.status]
    );
  const controllerValue: DataMutationShippingMemberSetAuthorityToLeave_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingMemberSetAuthorityToLeaveContext.Provider
      value={value}
    >
      <DataMutationShippingMemberSetAuthorityToLeave_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingMemberSetAuthorityToLeave_ControllerContext.Provider>
    </DataMutationShippingMemberSetAuthorityToLeaveContext.Provider>
  );
};

export const useDataMutationShippingMemberSetAuthorityToLeave = () => {
  const context = React.useContext(
    DataMutationShippingMemberSetAuthorityToLeaveContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingMemberSetAuthorityToLeave must be used within a DataMutationShippingMemberSetAuthorityToLeaveProvider"
    );
  }
  return context;
};

export const useDataMutationShippingMemberSetAuthorityToLeave_asController =
  () => {
    const context = React.useContext(
      DataMutationShippingMemberSetAuthorityToLeave_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutationShippingMemberSetAuthorityToLeave_asController must be used within a DataMutationShippingMemberSetAuthorityToLeaveProvider"
      );
    }
    return context;
  };
